/**
 * @module ui/site-header
 */
import ee from "lib/eventemitter";
import tgamConfig from "config/tgam";
import { qs, qsa, getById, interceptEventCallback, getProp } from "utils/utils";
import KeyboardTrap from "utils/keyboard-trap";
import ScrollMenuPrototype from "ui/scroll-menu";
import { isFullChartPage } from "lib/commercial/keytar/helpers";

import initFacetSearchSticky from "ui/facet-search-sticky";
import initSectionHeader from "ui/site-header--section";
import initHomeHeader from "ui/site-header--home";

import { load as loadHeaderAccordion, loaded as headerAccordionLoaded } from "ui/header-accordion";
import { closeLightBox, openLightBox } from "ui/lightbox";

import logger from "utils/logger";
import { isMobile } from "utils/detect";

const debugTag = "[SITE-HEADER]";

// From tgam-patterns: _trumps.no-scroll.scss
export const uNoScrollClass = "u-no-scroll"; // for <body> element
export const uNoScrollNotFixedClass = "u-no-scroll--not-fixed"; // for <body> element

const headerMenuPanelOpen = "c-site-header--menu-panel-open";
const menuPanelHiding = "c-site-header__menu-panel--hiding";
const menuPanelRevealing = "c-site-header__menu-panel--revealing";
const menuSubscribeHiding = "u-hidden";

// html elements
let header;
let menuOpenButtons;
let menuCloseButtons;
let menuPanel;
let menuPanelChild;
let menuPanelSearchPane;
let menuPanelCategoriesPane;
let lastClickedMenuOpenButton;
let headerSubscribeMenu;
let headerSubscribeBtn;
let headerSubscribeCards = [];
let keyboardTrap;

/**
 * @method siteHeaderInit
 * @description Init the sticky animation immediately, but delay the menu until clicked.
 */
export function siteHeaderInit() {
  header = qs(".c-site-header");
  if (!header) {
    logger.info(`${debugTag} init aborted`);
    return;
  } else {
    logger.info(`${debugTag} init`);
  }

  const headerType = getHeaderType();
  stickyInit(headerType);

  // Initialize the site header when clicked
  interceptEventCallback(header, "click", menuInit);

  // Initialize Scroll Menu
  const scrollMenu = Object.create(ScrollMenuPrototype);
  scrollMenu.init(qs(".c-site-header .c-scroll-menu"));
}

/**
 * @method stickyInit
 * @param {String} headerType - section or home
 * @description Hide and show animation for site header bar.
 */
function stickyInit(headerType) {
  // Additional, header-specific initialization for each type of header
  if (headerType === "home") {
    initHomeHeader(header, hideHeaderMenus);
  } else {
    // Financial full-chart pages don't need a sticky header.
    // Note, the Mutual Funds full-chart page uses "section" header, so that one is sticky.
    const notFullChartPage = !isFullChartPage();

    // Election Results page doesn't need to be sticky. Don't want to cover up results.
    const notElectionPage = getProp(tgamConfig, "meta.pagesubtype", "") !== "election";

    if (notFullChartPage && notElectionPage) {
      const isStoryPage = getProp(tgamConfig, "meta.isStoryPage", false);
      const isVideoPage = getProp(tgamConfig, "meta.isVideoPage", false);
      const isArticlePage = isStoryPage || isVideoPage;
      const scrollUpThreshold = isArticlePage ? 50 : 0; // pixels
      initSectionHeader(header, hideHeaderMenus, scrollUpThreshold);
      initFacetSearchSticky(header);
    }
  }
}

/**
 * @method menuInit
 * @description Common menu and child components for all header types
 */
function menuInit() {
  menuPanel = qs(".c-site-header__menu-panel");
  menuPanelChild = qs(".c-site-header__menu-panel > .c-menu-panel");
  menuOpenButtons = qsa("[aria-controls='site-header-menu-panel']");
  menuCloseButtons = qsa(".c-menu-panel__close-button .c-button");
  menuPanelSearchPane = qs(".c-menu-panel__page--search");
  menuPanelCategoriesPane = qs(".c-menu-panel__page--categories");

  if (!menuPanel
    || !menuOpenButtons.length
    || !menuCloseButtons.length
    || !menuPanelSearchPane
    || !menuPanelCategoriesPane) {
    logger.info(`${debugTag} menu aborted`);
    return;
  } else {
    logger.info(`${debugTag} menu`);
  }

  keyboardTrap = new KeyboardTrap([
    {
      item: getById("top-furniture-area"),
      type: "container"
    }
  ]);

  // Initialize Menu Panel
  menuOpenButtons.forEach(button => {
    button.addEventListener("click", () => {
      onMenuOpenButtonClick(button);
    });
  });
  menuCloseButtons.forEach(button => {
    button.addEventListener("click", closeMenuPanel);
  });
  menuPanel.addEventListener("animationend", onMenuPanelAnimationEnd);

  // Clicking outside the menu panel will close it
  // The click must also below the secondary nav
  menuPanel.addEventListener("click", closeMenuPanel);
  menuPanelChild.addEventListener("click", e => e.stopPropagation());
}

/**
 * @method getHeaderType
 * @description Determine the type of header that's on the page
 * @returns {String} of the type of header or an empty string
 */
function getHeaderType() {
  if (qs(".c-site-header--home")) {
    return "home";
  }
  return "section";
}

/**
 * @method hideHeaderMenus
 * @description Hide the Member Benefits menu
 * @returns {Boolean} true if hamburger menu is open
 */
export function hideHeaderMenus() {
  if (headerSubscribeMenu && !headerSubscribeMenu.classList.contains(menuSubscribeHiding)) {
    toggleSubscribeMenu(false);
  }
  return document.body.classList.contains(uNoScrollNotFixedClass);
}

/**
 * @method toggleSubscribeMenu
 * @description Toggle the Subscribe menu
 * @param {Boolean} hidden - true if currently hidden
 */
function toggleSubscribeMenu(hidden) {
  // accessibility helpers
  const preventTabFocus = c => c.setAttribute("tabindex", "-1");
  const allowTabFocus = c => c.removeAttribute("tabindex");

  window.requestAnimationFrame(() => {
    headerSubscribeMenu.classList[hidden ? "remove" : "add"](menuSubscribeHiding);
    headerSubscribeCards.forEach(hidden ? allowTabFocus : preventTabFocus);

    headerSubscribeMenu.setAttribute("aria-hidden", !hidden);
    headerSubscribeMenu.setAttribute("aria-expanded", hidden);
    headerSubscribeBtn.setAttribute("aria-pressed", hidden);
  });
}

/**
 * @method onMenuOpenButtonClick
 * @description Button click handler that decides whether to open the menu panel,
 * close the menu panel, or hide/reveal one of the panes within the menu panel.
 * @param {HTMLElement} button - The button that was clicked.
 */
function onMenuOpenButtonClick(button) {
  hideHeaderMenus();
  lastClickedMenuOpenButton = button;
  const burgerClicked = button.parentElement.classList.contains("c-site-header__hamburger");
  if (menuPanel.hasAttribute("hidden")) {
    // Panel is closed
    openMenuPanel();
    if (burgerClicked) {
      revealCategoriesPane();
    } else {
      // Search button was clicked
      revealSearchPane();
    }
  } else {
    // Panel is open
    if (burgerClicked) {
      if (menuPanelCategoriesPane.hasAttribute("hidden")) {
        revealCategoriesPane();
      } else {
        closeMenuPanel();
      }
    } else {
      // Search button was clicked
      if (menuPanelSearchPane.hasAttribute("hidden")) {
        revealSearchPane();
      } else {
        closeMenuPanel();
      }
    }
  }
}

/**
 * @method openMenuPanel
 * @description Opens the menu panel by adding/removing various classes and
 * attributes (which triggers CSS animation). Adds keyboard and click handlers
 * to close the menu panel. Locks the ability to scroll the page.
 */
function openMenuPanel() {
  if (!menuPanel.classList.contains(menuPanelRevealing)) {
    menuPanel.classList.remove(menuPanelHiding);
    menuPanel.removeAttribute("hidden");
    menuPanel.classList.add(menuPanelRevealing);
    menuOpenButtons.forEach((button) => {
      button.setAttribute("aria-expanded", true);
      button.setAttribute("aria-label", button.getAttribute("aria-label").replace("Open", "Close"));
    });
    document.body.classList.add(uNoScrollNotFixedClass);
    document.addEventListener("keydown", onKeyPress, false);
    // Adds click handler that closes panel when user clicks outside of it
    openLightBox({
      lightboxClasses: ["c-lightbox--transparent", "c-lightbox--menu-panel"],
      onCloseFunction: closeMenuPanel
    });
    keyboardTrap.activate();
  }
}

/**
 * @method closeMenuPanel
 * @description Closes the menu panel by adding/removing various classes and
 * attributes (which triggers CSS animation). Removes keyboard and click handlers.
 */
function closeMenuPanel() {
  if (!menuPanel.classList.contains(menuPanelHiding) && !menuPanel.hasAttribute("hidden")) {
    menuPanel.classList.remove(menuPanelRevealing);
    menuPanel.classList.add(menuPanelHiding);
    menuOpenButtons.forEach((button) => {
      button.setAttribute("aria-expanded", false);
      button.setAttribute("aria-label", button.getAttribute("aria-label").replace("Close", "Open"));
    });
    header.classList.remove(headerMenuPanelOpen);
    document.removeEventListener("keydown", onKeyPress);
    // Removes click handler that closes panel when user clicks outside of it
    closeLightBox();
    keyboardTrap.deactivate();
  }
}

/**
 * @method onMenuPanelAnimationEnd
 * @description Adds/removes various classes and attributes when the menu panel
 * has finished opening/closing and applies focus to the appropriate element.
 * Restores the ability to scroll the page.
 */
function onMenuPanelAnimationEnd() {
  if (menuPanel.classList.contains(menuPanelRevealing)) {
    // Menu panel has finished opening
    menuPanel.classList.remove(menuPanelRevealing);
    header.classList.add(headerMenuPanelOpen);

    ee.trigger("menu-panel:opened");
  } else if (menuPanel.classList.contains(menuPanelHiding)) {
    // Menu panel has finished closing
    menuPanel.classList.remove(menuPanelHiding);
    menuPanel.setAttribute("hidden", "hidden");
    lastClickedMenuOpenButton.focus();
    document.body.classList.remove(uNoScrollNotFixedClass);

    ee.trigger("menu-panel:closed");
    ee.trigger("analytics.trackGTMEvent", [{
      feature: "panel",
      type: "button",
      action: "close",
      label: "burger/search"
    }]);
  }
}

/**
 * @method onKeyPress
 * @description Closes the menu panel when the Escape key is pressed
 * @param {KeyboardEvent} event - A keyboard event
 */
function onKeyPress(event) {
  if (event.keyCode === 27) { // Escape key
    closeMenuPanel();
  }
}

/**
 * @method revealCategoriesPane
 * @description Reveals the "categories" menu panel pane and applies focus
 * to the menu panel close button
 */
function revealCategoriesPane() {
  menuPanelChild.classList.remove("c-menu-panel--light");
  menuPanelChild.classList.add("c-menu-panel--dark");
  menuPanelChild.classList.remove("c-menu-panel--no-footer");
  menuPanelChild.classList.add("c-menu-panel--has-footer");

  menuPanelCategoriesPane.removeAttribute("hidden");
  menuPanelSearchPane.setAttribute("hidden", "hidden");

  menuCloseButtons[0].classList.add("c-button--grey-secondary");
  menuCloseButtons[0].focus();

  ee.trigger("analytics.trackGTMEvent", [{
    feature: "panel",
    type: "button",
    action: "open",
    label: "burger"
  }]);

  if (!headerAccordionLoaded) {
    loadHeaderAccordion();
  }
}

/**
 * @method revealSearchPane
 * @description Reveals the "search" menu panel pane and applies focus to
 * the menu panel close button
 */
function revealSearchPane() {
  menuPanelChild.classList.remove("c-menu-panel--dark");
  menuPanelChild.classList.add("c-menu-panel--light");
  menuPanelChild.classList.remove("c-menu-panel--has-footer");
  menuPanelChild.classList.add("c-menu-panel--no-footer");

  menuPanelCategoriesPane.setAttribute("hidden", "hidden");
  menuPanelSearchPane.removeAttribute("hidden");

  menuCloseButtons[0].classList.remove("c-button--grey-secondary");

  // only focus on input for non-mobile search
  if (isMobile()) {
    menuCloseButtons[0].focus();
  } else {
    getById("search-input").focus();
  }

  ee.trigger("analytics.trackGTMEvent", [{
    feature: "panel",
    type: "button",
    action: "open",
    label: "search"
  }]);
}
